/**
 * AI Skill Sheet Analyzer Service
 * 
 * AIを活用してスキルシートの構造を理解し、柔軟にデータを抽出するサービス
 * 主な機能:
 * - 自然言語によるヘッダー理解
 * - コンテキストに基づくデータ推測
 * - 構造化されていないデータの整理
 * - 複数形式のスキルシートに対応
 * 
 * @service AISkillSheetAnalyzer
 */

import { SkillSheetData } from './skillSheetImportService';

export interface AIAnalysisResult {
  success: boolean;
  data?: SkillSheetData[];
  insights: string[];
  suggestions: string[];
  confidence: number;
}

export interface SheetStructure {
  headers: { [key: string]: number };
  dataPattern: 'single_row' | 'multi_row' | 'mixed';
  skillsFormat: 'comma_separated' | 'multiple_columns' | 'nested_table';
  confidence: number;
}

export class AISkillSheetAnalyzer {
  
  /**
   * AIによるスキルシート全体の分析
   */
  static async analyzeSheet(rawData: any[][]): Promise<AIAnalysisResult> {
    try {
      // シート構造の分析
      const structure = await this.analyzeStructure(rawData);
      
      // データの抽出
      const extractedData = await this.extractDataWithAI(rawData, structure);
      
      // 品質チェックと改善提案
      const insights = this.generateInsights(rawData, structure);
      const suggestions = this.generateSuggestions(extractedData);
      
      return {
        success: extractedData.length > 0,
        data: extractedData,
        insights,
        suggestions,
        confidence: structure.confidence
      };
    } catch (error) {
      return {
        success: false,
        insights: [`分析中にエラーが発生しました: ${(error as Error).message}`],
        suggestions: ['ファイル形式を確認してください'],
        confidence: 0
      };
    }
  }
  
  /**
   * シート構造の分析
   */
  private static async analyzeStructure(rawData: any[][]): Promise<SheetStructure> {
    if (!rawData || rawData.length === 0) {
      throw new Error('データが空です');
    }
    
    // ヘッダー候補の検出
    const headers = this.detectHeaders(rawData);
    
    // データパターンの分析
    const dataPattern = this.analyzeDataPattern(rawData);
    
    // スキル形式の分析
    const skillsFormat = this.analyzeSkillsFormat(rawData, headers);
    
    // 信頼度の計算
    const confidence = this.calculateConfidence(headers, dataPattern, rawData);
    
    return {
      headers,
      dataPattern,
      skillsFormat,
      confidence
    };
  }
  
  /**
   * ヘッダーの検出（AIロジック）
   */
  private static detectHeaders(rawData: any[][]): { [key: string]: number } {
    const headerMap: { [key: string]: number } = {};
    
    // 拡張されたヘッダーパターン（より柔軟に）
    const patterns = {
      name: [
        '名前', '氏名', '社員名', 'なまえ', '担当者', '担当者名',
        'name', 'full_name', 'fullname', 'engineer_name', 'person_name'
      ],
      email: [
        'メール', 'メールアドレス', 'email', 'mail', 'e-mail', 'メアド',
        'email_address', 'contact_email', '連絡先メール'
      ],
      phone: [
        '電話', '電話番号', 'phone', 'tel', 'telephone', '携帯', '携帯電話',
        'mobile', 'contact', '連絡先', 'phone_number'
      ],
      location: [
        '住所', '勤務地', '所在地', '場所', '拠点', 'location', 'address', 
        'workplace', 'office', '勤務先', '居住地'
      ],
      experience_years: [
        '経験年数', '経験', '年数', '実務経験', '業務経験', '開発経験',
        'experience', 'years', 'experience_years', 'work_experience'
      ],
      hourly_rate: [
        '時給', '単価', '時間単価', '料金', '報酬', 'rate', 'hourly_rate', 
        'hourly', 'price', 'cost', '時間あたり'
      ],
      availability_status: [
        '状況', 'ステータス', '稼働状況', '状態', '可用性', '参画可能',
        'status', 'availability', 'working_status', 'current_status'
      ],
      remote_work_preference: [
        'リモート', 'リモートワーク', '在宅', 'テレワーク', '遠隔',
        'remote', 'remote_work', 'work_from_home', 'telework'
      ],
      bio: [
        '自己紹介', '経歴', 'プロフィール', '概要', '説明', '詳細',
        'bio', 'profile', 'description', 'summary', 'about', 'background'
      ],
      skill_name: [
        'スキル', 'スキル名', '技術', '技術スキル', '使用技術', '得意技術',
        'skill', 'skill_name', 'technology', 'tech', 'expertise'
      ],
      skill_level: [
        'レベル', 'スキルレベル', '習熟度', '技術レベル', 'レベル感',
        'level', 'skill_level', 'proficiency', 'mastery', 'expertise_level'
      ],
      skill_years: [
        'スキル経験年数', 'スキル年数', '技術経験', '使用年数',
        'skill_years', 'years_experience', 'skill_experience', 'usage_years'
      ]
    };
    
    // 複数行でヘッダーを検索（最初の3行まで）
    const searchRows = rawData.slice(0, Math.min(3, rawData.length));
    
    for (const row of searchRows) {
      row.forEach((cell: any, index: number) => {
        if (!cell) return;
        
        const cellText = cell.toString().toLowerCase().trim();
        
        // より柔軟なマッチング
        Object.entries(patterns).forEach(([key, patternList]) => {
          if (headerMap[key] !== undefined) return; // 既に見つかっている場合はスキップ
          
          for (const pattern of patternList) {
            if (this.fuzzyMatch(cellText, pattern.toLowerCase())) {
              headerMap[key] = index;
              break;
            }
          }
        });
      });
      
      // 十分なヘッダーが見つかった場合は終了
      if (Object.keys(headerMap).length >= 3) break;
    }
    
    return headerMap;
  }
  
  /**
   * 曖昧マッチング（部分一致、類似性）
   */
  private static fuzzyMatch(text: string, pattern: string): boolean {
    // 完全一致
    if (text === pattern) return true;
    
    // 部分一致
    if (text.includes(pattern) || pattern.includes(text)) return true;
    
    // 文字の順序を無視した部分的な一致
    const textChars = text.replace(/[^ぁ-んァ-ヶa-zA-Z0-9]/g, '');
    const patternChars = pattern.replace(/[^ぁ-んァ-ヶa-zA-Z0-9]/g, '');
    
    if (textChars.includes(patternChars) || patternChars.includes(textChars)) {
      return true;
    }
    
    return false;
  }
  
  /**
   * データパターンの分析
   */
  private static analyzeDataPattern(rawData: any[][]): 'single_row' | 'multi_row' | 'mixed' {
    if (rawData.length <= 2) return 'single_row';
    
    // データの密度を分析
    let filledCells = 0;
    let totalCells = 0;
    
    const dataRows = rawData.slice(1); // ヘッダー以外
    
    dataRows.forEach(row => {
      row.forEach(cell => {
        totalCells++;
        if (cell && cell.toString().trim() !== '') {
          filledCells++;
        }
      });
    });
    
    const density = filledCells / totalCells;
    
    if (density > 0.7) return 'single_row';
    if (density > 0.3) return 'mixed';
    return 'multi_row';
  }
  
  /**
   * スキル形式の分析
   */
  private static analyzeSkillsFormat(rawData: any[][], headers: { [key: string]: number }): 'comma_separated' | 'multiple_columns' | 'nested_table' {
    if (!rawData.length) return 'comma_separated';
    
    // スキル関連のヘッダー数をチェック
    const skillHeaders = Object.keys(headers).filter(key => key.includes('skill'));
    
    if (skillHeaders.length >= 3) return 'multiple_columns';
    
    // データ行でカンマ区切りをチェック
    const dataRows = rawData.slice(1);
    let commaCount = 0;
    
    dataRows.slice(0, 5).forEach(row => { // サンプル5行をチェック
      row.forEach(cell => {
        if (cell && cell.toString().includes(',')) {
          commaCount++;
        }
      });
    });
    
    if (commaCount >= 2) return 'comma_separated';
    
    return 'nested_table';
  }
  
  /**
   * 信頼度の計算
   */
  private static calculateConfidence(headers: { [key: string]: number }, dataPattern: string, rawData: any[][]): number {
    let confidence = 0;
    
    // ヘッダー認識度
    const requiredHeaders = ['name'];
    const optionalHeaders = ['email', 'phone', 'skill_name', 'experience_years'];
    
    let foundRequired = 0;
    let foundOptional = 0;
    
    requiredHeaders.forEach(header => {
      if (headers[header] !== undefined) foundRequired++;
    });
    
    optionalHeaders.forEach(header => {
      if (headers[header] !== undefined) foundOptional++;
    });
    
    confidence += (foundRequired / requiredHeaders.length) * 0.5; // 50%重み
    confidence += (foundOptional / optionalHeaders.length) * 0.3; // 30%重み
    
    // データ量
    const dataRowCount = rawData.length - 1;
    if (dataRowCount > 0) confidence += 0.2; // 20%重み
    
    return Math.min(confidence, 1.0);
  }
  
  /**
   * AIによるデータ抽出
   */
  private static async extractDataWithAI(rawData: any[][], structure: SheetStructure): Promise<SkillSheetData[]> {
    const extractedData: SkillSheetData[] = [];
    const { headers, skillsFormat } = structure;
    
    // ヘッダー行をスキップしてデータ行を処理
    const dataRows = rawData.slice(1);
    
    for (let i = 0; i < dataRows.length; i++) {
      const row = dataRows[i];
      
      if (this.isEmptyRow(row)) continue;
      
      try {
        const engineerData = await this.extractRowDataWithAI(row, headers, skillsFormat, i + 2);
        if (engineerData) {
          extractedData.push(engineerData);
        }
      } catch (error) {
        console.warn(`行 ${i + 2} の処理中にエラー:`, error);
        // エラーがあっても他の行は処理を続行
      }
    }
    
    return extractedData;
  }
  
  /**
   * 行データのAI抽出
   */
  private static async extractRowDataWithAI(
    row: any[], 
    headers: { [key: string]: number }, 
    skillsFormat: string, 
    rowNumber: number
  ): Promise<SkillSheetData | null> {
    
    // 基本情報の抽出
    const name = this.extractCellValue(row, headers.name);
    if (!name || name.trim() === '') {
      return null; // 名前は必須
    }
    
    const email = this.extractCellValue(row, headers.email);
    const phone = this.extractCellValue(row, headers.phone);
    const location = this.extractCellValue(row, headers.location);
    const bio = this.extractCellValue(row, headers.bio);
    
    // 数値データの抽出と推測
    const experience_years = this.smartParseNumber(this.extractCellValue(row, headers.experience_years));
    const hourly_rate = this.smartParseNumber(this.extractCellValue(row, headers.hourly_rate));
    
    // ステータスの推測
    const availability_status = this.intelligentParseStatus(
      this.extractCellValue(row, headers.availability_status)
    );
    
    // リモートワーク設定の推測
    const remote_work_preference = this.intelligentParseBoolean(
      this.extractCellValue(row, headers.remote_work_preference)
    );
    
    // スキルの抽出
    const skills = await this.extractSkillsWithAI(row, headers, skillsFormat);
    
    return {
      name: name.trim(),
      email: email || undefined,
      phone: phone || undefined,
      location: location || undefined,
      experience_years,
      hourly_rate,
      availability_status,
      remote_work_preference,
      bio: bio || undefined,
      skills
    };
  }
  
  /**
   * スマートなセル値抽出
   */
  private static extractCellValue(row: any[], columnIndex: number | undefined): string {
    if (columnIndex === undefined || columnIndex >= row.length) {
      return '';
    }
    const cellValue = row[columnIndex];
    if (cellValue === null || cellValue === undefined) {
      return '';
    }
    return cellValue.toString().trim();
  }
  
  /**
   * 賢い数値解析
   */
  private static smartParseNumber(value: string): number | undefined {
    if (!value || value === '') return undefined;
    
    // 日本語数字の処理
    const japaneseNumbers: { [key: string]: string } = {
      '一': '1', '二': '2', '三': '3', '四': '4', '五': '5',
      '六': '6', '七': '7', '八': '8', '九': '9', '十': '10'
    };
    
    let processedValue = value;
    Object.entries(japaneseNumbers).forEach(([jp, num]) => {
      processedValue = processedValue.replace(new RegExp(jp, 'g'), num);
    });
    
    // 数字以外を除去
    const numberMatch = processedValue.match(/\d+(\.\d+)?/);
    if (numberMatch) {
      const num = parseFloat(numberMatch[0]);
      return isNaN(num) ? undefined : num;
    }
    
    return undefined;
  }
  
  /**
   * インテリジェントなステータス解析
   */
  private static intelligentParseStatus(value: string): 'available' | 'busy' | 'unavailable' {
    if (!value) return 'available';
    
    const normalized = value.toLowerCase().trim();
    
    // 忙しい状態の表現
    const busyPatterns = [
      '忙しい', '稼働中', 'busy', 'occupied', '参画中', '対応中', 
      'working', 'assigned', '×', '不可', '無理'
    ];
    
    // 利用不可の表現
    const unavailablePatterns = [
      '稼働不可', 'unavailable', 'not available', '休止中', '休憩中',
      'offline', '対応不可', '参画不可'
    ];
    
    for (const pattern of busyPatterns) {
      if (normalized.includes(pattern)) return 'busy';
    }
    
    for (const pattern of unavailablePatterns) {
      if (normalized.includes(pattern)) return 'unavailable';
    }
    
    return 'available';
  }
  
  /**
   * インテリジェントなブール値解析
   */
  private static intelligentParseBoolean(value: string): boolean {
    if (!value) return false;
    
    const normalized = value.toLowerCase().trim();
    
    const truePatterns = [
      'true', '1', 'yes', 'はい', 'ok', '○', '可', '可能', 'できる',
      'available', 'allowed', '希望', '希望する', '好む'
    ];
    
    const falsePatterns = [
      'false', '0', 'no', 'いいえ', 'ng', '×', '不可', '不可能', 'できない',
      'not available', 'not allowed', '希望しない', '好まない'
    ];
    
    for (const pattern of truePatterns) {
      if (normalized.includes(pattern)) return true;
    }
    
    for (const pattern of falsePatterns) {
      if (normalized.includes(pattern)) return false;
    }
    
    return false;
  }
  
  /**
   * AIによるスキル抽出
   */
  private static async extractSkillsWithAI(
    row: any[], 
    headers: { [key: string]: number }, 
    skillsFormat: string
  ): Promise<SkillSheetData['skills']> {
    const skills: SkillSheetData['skills'] = [];
    
    // 基本的なスキル情報の抽出
    const skillName = this.extractCellValue(row, headers.skill_name);
    const skillLevel = this.extractCellValue(row, headers.skill_level);
    const skillYears = this.extractCellValue(row, headers.skill_years);
    
    if (skillName) {
      if (skillName.includes(',') || skillName.includes('、')) {
        // カンマ区切りスキルの処理
        const skillList = skillName.split(/[,、]/).map(s => s.trim()).filter(s => s.length > 0);
        
        skillList.forEach(skill => {
          skills.push({
            skill_name: skill,
            skill_level: this.parseSkillLevel(skillLevel),
            years_experience: this.smartParseNumber(skillYears)
          });
        });
      } else {
        // 単一スキル
        skills.push({
          skill_name: skillName,
          skill_level: this.parseSkillLevel(skillLevel),
          years_experience: this.smartParseNumber(skillYears)
        });
      }
    }
    
    // 他の列からのスキル検出（スキル1、スキル2など）
    row.forEach((cell: any, index: number) => {
      if (!cell || index === headers.skill_name) return;
      
      const cellValue = cell.toString().trim();
      
      // プログラミング言語やフレームワークらしい文字列を検出
      if (this.looksLikeSkill(cellValue) && !skills.some(s => s.skill_name === cellValue)) {
        skills.push({
          skill_name: cellValue,
          skill_level: 'intermediate',
          years_experience: 1
        });
      }
    });
    
    return skills;
  }
  
  /**
   * スキルらしい文字列の判定
   */
  private static looksLikeSkill(value: string): boolean {
    if (!value || value.length < 2) return false;
    
    const commonSkills = [
      'javascript', 'typescript', 'python', 'java', 'c#', 'php', 'ruby', 'go',
      'swift', 'kotlin', 'react', 'vue', 'angular', 'node.js', 'express',
      'django', 'flask', 'spring', 'laravel', 'rails', 'mysql', 'postgresql',
      'mongodb', 'redis', 'aws', 'azure', 'gcp', 'docker', 'kubernetes', 'git'
    ];
    
    const normalized = value.toLowerCase();
    
    // 既知のスキルとの一致
    if (commonSkills.some(skill => normalized.includes(skill) || skill.includes(normalized))) {
      return true;
    }
    
    // 技術っぽいパターン（大文字小文字混在、.js等の拡張子）
    if (/[A-Z]/.test(value) && /[a-z]/.test(value)) return true;
    if (/\.(js|ts|py|rb|php|go)$/.test(normalized)) return true;
    
    return false;
  }
  
  /**
   * スキルレベルの解析
   */
  private static parseSkillLevel(value: string): 'beginner' | 'intermediate' | 'advanced' | 'expert' {
    if (!value) return 'intermediate';
    
    const normalized = value.toLowerCase().trim();
    
    if (normalized.includes('初級') || normalized.includes('beginner') || normalized.includes('1')) {
      return 'beginner';
    } else if (normalized.includes('上級') || normalized.includes('advanced') || normalized.includes('3')) {
      return 'advanced';
    } else if (normalized.includes('エキスパート') || normalized.includes('expert') || normalized.includes('4')) {
      return 'expert';
    }
    
    return 'intermediate';
  }
  
  /**
   * 空行判定
   */
  private static isEmptyRow(row: any[]): boolean {
    return row.every(cell => !cell || cell.toString().trim() === '');
  }
  
  /**
   * インサイト生成
   */
  private static generateInsights(rawData: any[][], structure: SheetStructure): string[] {
    const insights: string[] = [];
    
    insights.push(`データ構造を分析しました (信頼度: ${Math.round(structure.confidence * 100)}%)`);
    insights.push(`${Object.keys(structure.headers).length}個のヘッダーを認識しました`);
    insights.push(`${rawData.length - 1}行のデータを検出しました`);
    insights.push(`スキル形式: ${structure.skillsFormat}`);
    
    return insights;
  }
  
  /**
   * 提案生成
   */
  private static generateSuggestions(data: SkillSheetData[]): string[] {
    const suggestions: string[] = [];
    
    if (data.length === 0) {
      suggestions.push('データを抽出できませんでした。ヘッダー行に「名前」列が含まれているか確認してください。');
    } else {
      suggestions.push(`${data.length}件のエンジニアデータを抽出しました。`);
      
      const noEmailCount = data.filter(d => !d.email).length;
      if (noEmailCount > 0) {
        suggestions.push(`${noEmailCount}件でメールアドレスが不足しています。`);
      }
      
      const noSkillsCount = data.filter(d => d.skills.length === 0).length;
      if (noSkillsCount > 0) {
        suggestions.push(`${noSkillsCount}件でスキル情報が不足しています。`);
      }
    }
    
    return suggestions;
  }
}

export default AISkillSheetAnalyzer;